import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, ChevronRight, Activity, Users, FileText, DollarSign } from 'lucide-react';
export function Hero() {
  return <section className="relative min-h-[100svh] flex items-center pt-28 pb-12 lg:pt-32 lg:pb-24 overflow-hidden bg-navy" data-id="element-221">
      {/* Background Image with Stronger Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-222">
        <img src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https%3A//assets.cdn.filesafe.space/cMkAnz3QfJpQ1Lp2u0QG/media/69160e581547ed5536c65e53.jpg" alt="Trucking Operations" className="w-full h-full object-cover object-center scale-105" data-id="element-223" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70" data-id="element-224"></div>
        <div className="absolute inset-0 bg-black/50" data-id="element-225"></div>
      </div>

      <div className="container-custom relative z-10 w-full" data-id="element-226">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center" data-id="element-227">
          {/* Main Content */}
          <div className="lg:col-span-7 xl:col-span-7" data-id="element-228">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-229">
              <span className="inline-block py-1.5 px-3 bg-red/10 border border-red/30 text-red font-bold uppercase tracking-widest text-xs mb-6 rounded-sm backdrop-blur-sm" data-id="element-230">
                For Trucking Company Owners & Fleet Operators
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-extrabold text-white leading-[1.05] mb-6 tracking-tight" data-id="element-231">
                Scale Your Trucking Company Without Staying Trapped in
                Day-to-Day Operations
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-white/80 mb-8 max-w-2xl leading-relaxed font-medium" data-id="element-232">
                FleetScale finds the bottlenecks slowing your trucking business
                down, then installs the systems, operating rhythm, technology,
                and accountability needed to grow with less chaos.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-233">
                <a href="#contact" className="btn-primary group text-base py-4 px-8 shadow-[0_0_20px_rgba(227,40,54,0.4)]" data-id="element-234">
                  Book a Free 1:1 Consultation
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-235" />
                </a>
                <a href="#framework" className="btn-secondary py-4 px-8" data-id="element-236">
                  See the FleetScale Framework
                </a>
              </div>

              {/* Proof Chips - visible above fold */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3" data-id="element-237">
                {['50+ Years Combined Transportation Experience', '100+ Proven Framework Implementations', 'Driver Recruiting, Dispatch, Back Office, Compliance & Technology', '3X ROI Guarantee on Qualifying 12-Month Programs'].map((chip, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 0.3 + i * 0.1
              }} className="flex items-start bg-white/5 p-2 rounded border border-white/10" data-id="element-238">
                    <CheckCircle2 className="w-5 h-5 text-amber shrink-0 mr-2 mt-0.5" data-id="element-239" />
                    <span className="text-xs sm:text-sm text-white/90 leading-tight font-medium" data-id="element-240">
                      {chip}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>

          {/* Floating Operations Snapshot Card */}
          <div className="lg:col-span-5 xl:col-span-4 xl:col-start-9 mt-12 lg:mt-0" data-id="element-241">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.4
          }} className="bg-black/70 backdrop-blur-xl border border-white/10 rounded-sm p-6 shadow-2xl relative overflow-hidden" data-id="element-242">
              {/* Decorative top accent */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red to-amber" data-id="element-243"></div>

              <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-4" data-id="element-244">
                <h3 className="text-white font-heading font-bold text-lg flex items-center tracking-wide uppercase" data-id="element-245">
                  <Activity className="w-5 h-5 text-amber mr-2" data-id="element-246" />
                  Operations Snapshot
                </h3>
                <span className="flex h-2.5 w-2.5 relative" data-id="element-247">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber opacity-75" data-id="element-248"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber" data-id="element-249"></span>
                </span>
              </div>

              <div className="space-y-3" data-id="element-250">
                {[{
                icon: Activity,
                label: 'Dispatch',
                before: 'Reactive',
                after: 'Planned'
              }, {
                icon: Users,
                label: 'Recruiting',
                before: 'Churn',
                after: 'Pipeline'
              }, {
                icon: FileText,
                label: 'Back Office',
                before: 'Manual Work',
                after: 'Exception Control'
              }, {
                icon: DollarSign,
                label: 'Cash Flow',
                before: 'Delays',
                after: 'Measurable Rhythm'
              }].map((row, i) => <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-3.5 flex items-center justify-between group hover:bg-white/10 transition-colors" data-id="element-251">
                    <div className="flex items-center" data-id="element-252">
                      <div className="w-8 h-8 rounded-sm bg-navy flex items-center justify-center mr-3 border border-white/10" data-id="element-253">
                        <row.icon className="w-4 h-4 text-white/70" data-id="element-254" />
                      </div>
                      <span className="text-white font-bold text-sm tracking-wide" data-id="element-255">
                        {row.label}
                      </span>
                    </div>
                    <div className="flex items-center text-xs font-bold uppercase tracking-wider" data-id="element-256">
                      <span className="text-white/40" data-id="element-257">{row.before}</span>
                      <ChevronRight className="w-3 h-3 text-amber mx-2" data-id="element-258" />
                      <span className="text-amber" data-id="element-259">{row.after}</span>
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 pt-5 border-t border-white/10 text-center bg-white/5 -mx-6 -mb-6 p-4" data-id="element-260">
                <p className="text-white/90 italic text-sm font-bold tracking-wide" data-id="element-261">
                  "Your trucks should run 24/7 — not you."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}